import { createElement, useMemo } from "react";

import { useLabels } from "~/hooks/useLabels";

import { Icon } from "../icon/Icon";
import { Truncate } from "../truncate/Truncate";
import { Text } from "../text/Text";
import { Link } from "../link/Link";
import { ProgressBar } from "../progressBar/ProgressBar";
import { MetaDataList } from "../metaDataList/MetaDataList";

import "./QuizCard.css";

import type { CSSProperties, SyntheticEvent } from "react";
import type { IconVariantProp } from "../icon/Icon";
import type { LinkProps } from "../link/Link";

export interface QuizCardProps {
  artworkColor?: string;
  description?: string;
  href?: string;
  icon?: string;
  iconVariant?: IconVariantProp;
  meta?: string[];
  nativeBehavior?: LinkProps["nativeBehavior"];
  nativeStencil?: LinkProps["nativeStencil"];
  progress?: number;
  questionCount?: number;
  title?: string;
  onClick?: (e: SyntheticEvent) => void;
}

const DEFAULT_LABELS = {
  "card.quiz.questionCount_one": "{{count}} Question",
  "card.quiz.questionCount_other": "{{count}} Questions",
  "card.quiz.progress_one": "{{count}} question left",
  "card.quiz.progress_other": "{{count}} questions left",
};

export function QuizCard({
  artworkColor,
  description,
  href,
  icon = "list-check",
  iconVariant,
  meta,
  nativeBehavior,
  nativeStencil = {
    propsJSON: "{}",
    type: "session",
  },
  progress = 0,
  questionCount,
  title,
  onClick,
}: QuizCardProps) {
  const { t } = useLabels({ fallback: DEFAULT_LABELS });

  const progressLabel = useMemo(() => {
    if (progress && progress < 1 && questionCount) {
      return t("card.quiz.progress", {
        count: Math.ceil(questionCount - progress * questionCount),
      });
    }

    return t("card.quiz.questionCount", { count: questionCount });
  }, [progress, questionCount, t]);

  const children = useMemo(
    () => (
      <>
        <div className="QuizCard-artwork">
          <Icon
            icon={icon}
            variant={iconVariant}
            color="var(--color-white)"
            size="lg"
          />
        </div>

        <div className="QuizCard-body">
          <MetaDataList>
            {meta?.map((m) => (
              <MetaDataList.Item key={m}>{m}</MetaDataList.Item>
            ))}
          </MetaDataList>

          <Text preset="title" weight="semibold">
            <Truncate lines={2}>{title}</Truncate>
          </Text>

          <Text preset="description">
            <Truncate lines={3}>{description}</Truncate>
          </Text>
        </div>

        <div className="QuizCard-footer">
          <div className="QuizCard-progress">
            {progress > 0 && progress < 1 && (
              <ProgressBar size="sm" value={progress} />
            )}

            <Text size="2xs" weight="medium" lineHeight="tight">
              {progressLabel}
            </Text>
          </div>

          {progress === 1 && (
            <Icon
              icon="circle-check"
              variant="solid"
              size="sm"
              color="var(--color-brand-accessible)"
            />
          )}
        </div>
      </>
    ),
    [description, icon, iconVariant, meta, progress, progressLabel, title],
  );

  const baseProps = {
    children: children,
    className: "QuizCard",
    style: {
      "--card-artwork-color": artworkColor,
    } as CSSProperties,
    onClick,
  };

  if (href) {
    return createElement(Link, {
      ...baseProps,
      to: href,
      nativeBehavior,
      nativeStencil,
    });
  }

  if (onClick) {
    return createElement("button", {
      ...baseProps,
    });
  }

  return createElement("div", {
    ...baseProps,
  });
}
